import ConfirmationModal from "components/Common/ConfirmationModal";
import Render from "components/Common/Render";
import InputField from "components/FormFields/InputField";
import PreRegisterButtons from "components/Home/Preregister/PreRegisterButtons";
import PreRegisterCancellation from "components/Home/Preregister/PreRegisterCancellation";
import PromotionPoints from "components/Home/PromotionPoints";
import { amountOptions } from "constants/amountOptions";
import { PreSignupStatusEnum } from "enum/common";
import useMixpanel from "hooks/useMixpanel";
import { http } from "middleware/apiMiddleware";
import moment from "moment";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import ReactSelect from "react-select";

interface IPreRegisterDetailsProps {
  topupAmount: number;
  referralCode: string;
  selectedBusiness: BusinessType;
  setTopupAmount: (value: number) => void;
  setReferralCode: (value: string) => void;
  onNext: () => void;
  onCancelRegisteration: () => void;
  error:string;
  setErrorMessage: (value: string) => void;
}

const styles = {
  buttonStyle: "md:w-[200px] md:self-end self-center me-0",
  inputStyle:
    "border border-black20 rounded-md mt-2 px-4 font-poppins md:flex-1 !mb-[0px]",
  formFieldContainer:
    "flex-1 flex md:flex-row flex-col md:gap-4 md:items-center",
  formLabel: "text-semibold md:w-[250px]",
};

const PreRegisterDetails: React.FC<IPreRegisterDetailsProps> = ({
  onNext,
  referralCode,
  setReferralCode,
  topupAmount,
  setTopupAmount,
  selectedBusiness,
  onCancelRegisteration,
  error,
  setErrorMessage
}) => {
  const { trackEvent } = useMixpanel();
  const [showTopupError, setShowTopupError] = useState(false);
  const [showCancelConfirmationModal, setShowCancelConfirmationModal] =
    useState(false);
  const [isCancellingRegistration, setIsCancellingRegistration] =
    useState(false);
  const isPreRegistered =
    selectedBusiness?.preSignupDetails?.preSignupStatus ===
    PreSignupStatusEnum.ENROLLED;

  const cityName = selectedBusiness?.communities[0]?.cityName;

  const onContinue = () => {
    if (!topupAmount) {
      setShowTopupError(true);
      return;
    }
    onNext();
  };

  const topupAmountValue = useMemo(
    () =>
      amountOptions?.filter(
        (amountOption) =>
          amountOption?.value ===
          selectedBusiness?.preSignupDetails?.topUpAmount
      )[0] || amountOptions[0],
    [selectedBusiness?.preSignupDetails]
  );

  const activationDate = useMemo(
    () =>
      moment
        .utc(selectedBusiness?.preSignupDetails?.activationDate)
        .format("MMM Do"),
    [selectedBusiness?.preSignupDetails]
  );

  const deActivationDate = useMemo(
    () =>
      moment
        .utc(selectedBusiness?.preSignupDetails?.activationDate)
        .subtract(1, "days")
        .format("MMM Do"),
    [selectedBusiness?.preSignupDetails]
  );

  const onCancelRegistration = () => setShowCancelConfirmationModal(true);
  const onCancelConfirm = async () => {
    try {
      setShowCancelConfirmationModal(false);
      setIsCancellingRegistration(true);
      const { data, error } = await http.patch(
        `/business/${selectedBusiness?.uuid}/pre-signup/${selectedBusiness?.preSignupDetails?.uuid}/withdraw`,
        {}
      );
      if (!!data && !error) {
        toast("Registration cancelled successfully");
        onCancelRegisteration();
        setTopupAmount(amountOptions[0].value);
      }
    } catch (error) {
      toast.error(
        "Error Occurred while cancelling Registration, Please try again after sometime"
      );
      console.error("error occurred while cancelling registration", error);
    } finally {
      setIsCancellingRegistration(false);
    }
  };

  const onHideConfirmationModal = () => setShowCancelConfirmationModal(false);

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex-grow flex gap-6 flex-col">
        <div className="flex flex-1 flex-col gap-2">
          <p className={` text-navyBlue text-sm font-extralight leading-snug`}>
            {cityName}’s sponsored program will be ending {deActivationDate}.
            You can now pre-register to stay active in the app, continue to
            attract customers and connect with thousands of local shoppers near
            you!
            <p className="mt-2">
              If no action is taken, your business will be deactivated in the
              app on {activationDate}, so don’t miss out, pre-register today!
            </p>
          </p>

          <PromotionPoints
            customClass="!text-sm"
            heading={"How it works:"}
            points={[
              "Instead of having the city sponsoring the rewards, each business will set up its own rewards fund",
              "Shoppers will earn 5% rewards for each purchase they make with you, up to $5",
              "The rewards they earn will be deducted from your rewards fund",
              "Your rewards fund will be automatically topped up with your payment method when it falls below $25",
              "Only pay when customers have shopped with you",
              "Cancel any time",
            ]}
          />
        </div>
        <div className="flex flex-col md:items-center md:flex-row gap-4 sticky bottom-0 bg-white pt-2 border-t shadow-lg md:border-none md:shadow-none">
          <div className="gap-4 flex flex-col">
            <div className={styles.formFieldContainer}>
              <div className={`${styles.formLabel} mb-2 md:mb-0`}>
                Rewards Fund Top-Up Amount{" "}
              </div>
              <ReactSelect
                menuPlacement="top"
                isDisabled={isPreRegistered}
                isSearchable={false}
                defaultValue={topupAmountValue}
                autoFocus={false}
                placeholder="Select Amount"
                className="md:w-[280px]"
                isMulti={false}
                closeMenuOnSelect
                options={amountOptions}
                onChange={(selectedOption: any) => {
                  trackEvent("pre_register_popup_topup_amount_select", {
                    topUpAmount: selectedOption.value,
                  });
                  setTopupAmount(selectedOption.value);
                  setShowTopupError(false);
                }}
              />
              {(showTopupError || topupAmount <= 0) && (
                <p className="text-failed">Please Select Auto Top Up Amount</p>
              )}
            </div>
            <div className={`${styles.formFieldContainer} gap-0`}>
              <div className={styles.formLabel}>Referral Code <span className="gap-4 text-gray-400 text-sm  md:w-[250px]">(Optional)</span>  </div>
              <InputField
                containerClass="!mb-0 md:w-[280px]"
                customClass={`${styles.inputStyle} ${isPreRegistered || selectedBusiness.usedReferralCode  ? 'text-opacity-40' :''}`}
                placeholder="XXXXXX"
                onChange={function (e: any): void {
                  setErrorMessage('')
                  setReferralCode(e.target.value);
                }}
                extras={{
                  disabled: isPreRegistered || selectedBusiness.usedReferralCode,
                }}
                value={referralCode}
                label={""}
                id={""}
                error={error}
              />
            </div>
          </div>

          <div className="md:hidden">
            <PreRegisterButtons
              isPreRegistered={isPreRegistered}
              onContinue={onContinue}
              isCancellingRegistration={isCancellingRegistration}
              buttonStyle={styles.buttonStyle}
              onCancelRegistration={onCancelRegistration}
            />
          </div>
        </div>
      </div>
      <div className="mt-auto justify-center w-[100%] md:block hidden">
        <PreRegisterButtons
          isPreRegistered={isPreRegistered}
          onContinue={onContinue}
          isCancellingRegistration={isCancellingRegistration}
          buttonStyle={styles.buttonStyle}
          onCancelRegistration={onCancelRegistration}
        />
      </div>
      {/* <RenderSliderDots selectedStep={PreSigninStepsEnum.DETAIL} /> */}
      <Render when={showCancelConfirmationModal}>
        <ConfirmationModal
          show={showCancelConfirmationModal}
          onConfirmClick={onHideConfirmationModal}
          onHide={onCancelConfirm}
          renderConfirmationMessage={
            <PreRegisterCancellation activationDate={activationDate} />
          }
          yesButtonLabel="Stay Registered"
          noButtonLabel="Confirm Cancellation"
          customClass="md:w-[30vw] w-[70vw] !md:m-0 !p-0 !z-50 h-auto"
        />
      </Render>
    </div>
  );
};

export default PreRegisterDetails;
